// "Patient transfers from the PM" — Practice Details card, admins.
//
// The per-practice switch pm_patient_transfer_trusted (David, 2026-10-19; backend:
// services/pm_patient_transfer_service.py). ON (the default): a charge the PM moved
// to the patient (PracticeSuite's BILL_TO_PT) with no ERA for the claim is patient
// outstanding, labelled "per PM, no ERA", its own sub-bucket inside patient A/R on the
// Waterfall and the client report. OFF (strict): it stays insurance-side until an EOB
// says PR-1, in the "Moved to patient, no code" block. Either way an ERA wins once
// one exists for the claim. Nothing here names a patient: one boolean and a label.

import React, { useEffect, useRef, useState } from "react";
import "./PmPatientTransfer.css";

const API_URL = process.env.REACT_APP_API_URL || "";

const url = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/pm-patient-transfer`;

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || fallback);
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

export function fetchPmPatientTransfer(practiceId) {
  return request(
    url(practiceId),
    {},
    "Could not load the patient-transfer setting",
  );
}

export function savePmPatientTransfer(practiceId, trusted) {
  return request(
    url(practiceId),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trusted }),
    },
    "Could not save the patient-transfer setting",
  );
}

export const TRUSTED_TEXT =
  "Trusted: a charge the PM moved to the patient with no ERA for the claim is patient outstanding, labelled “per PM, no ERA” (its own line inside patient A/R, beside PR-1/2/3).";
export const STRICT_TEXT =
  "Strict: it stays insurance-side at the expected until an EOB says PR-1 (the “Moved to patient, no code” block).";

export default function PmPatientTransferCard({
  practiceId,
  readOnly = false,
}) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    setSummary(null);
    setError(null);
    fetchPmPatientTransfer(practiceId)
      .then((body) => {
        if (!cancelled) setSummary(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const change = async (value) => {
    if (busy || !summary) return;
    const next = value === "trusted";
    if (next === summary.trusted) return;
    setBusy(true);
    setError(null);
    try {
      const body = await savePmPatientTransfer(practiceId, next);
      if (currentPractice.current === practiceId) setSummary(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || readOnly || !summary || summary.migration_pending;

  return (
    <div
      className="integrations-section pm-patient-transfer"
      data-testid="pm-patient-transfer-card"
    >
      <div className="pm-patient-transfer-header">
        <h3>Patient transfers from the PM</h3>
        {summary && (
          <span
            className={`pm-patient-transfer-badge ${summary.trusted ? "on" : "off"}`}
          >
            {summary.migration_pending
              ? "Pending"
              : summary.trusted
                ? "Trusted"
                : "Strict"}
          </span>
        )}
      </div>
      <p className="pm-patient-transfer-explainer">
        What the Waterfall and the client report do with a charge the practice
        management system moved to the patient (PracticeSuite&apos;s BILL_TO_PT)
        when Atlas holds no ERA for the claim. Once an ERA exists it decides: a
        PR code makes the balance the patient&apos;s, a denial code makes it a
        denial.
      </p>
      {error && (
        <div className="pm-patient-transfer-error" role="alert">
          {error}
          <button
            type="button"
            className="message-close-sm"
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}
      {summary && summary.migration_pending ? (
        <p className="pm-patient-transfer-muted">
          Reads as trusted (the default) until migration {summary.migration} has
          run; it cannot be changed before then.
        </p>
      ) : null}
      <div className="pm-patient-transfer-row">
        <label>
          <input
            type="radio"
            name={`pm-patient-transfer-${practiceId}`}
            value="trusted"
            checked={Boolean(summary && summary.trusted)}
            disabled={disabled}
            onChange={() => change("trusted")}
          />{" "}
          {TRUSTED_TEXT}
        </label>
        <label>
          <input
            type="radio"
            name={`pm-patient-transfer-${practiceId}`}
            value="strict"
            checked={Boolean(summary && !summary.trusted)}
            disabled={disabled}
            onChange={() => change("strict")}
          />{" "}
          {STRICT_TEXT}
        </label>
      </div>
    </div>
  );
}
